import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Alert } from 'antd';
import { useNotifications } from '../../contexts/NotificationContext';
import './NotificationAlert.scss';

/**
 * NotificationAlert Component
 *
 * Renders real-time notifications from SSE stream using Ant Design Alert components.
 * Supports priority levels, custom actions, HTML content, and smooth animations.
 *
 * Features:
 * - Auto-dismiss with configurable duration
 * - Priority-based visual styling (low, normal, high, urgent)
 * - Action buttons with different types (default, primary, link)
 * - HTML content rendering support
 * - Smooth enter/exit animations
 * - Click-to-open URL support
 *
 * Expected notification structure:
 * {
 *   id: string,
 *   title: string,
 *   description: string,
 *   type: 'success'|'info'|'warning'|'error',
 *   priority: 'low'|'normal'|'high'|'urgent',
 *   autoClose: boolean,
 *   duration: number,
 *   actions: [{ label: string, actionUrl: string, type: string }],
 *   messageType: 'text'|'html',
 *   actionUrl: string
 * }
 */

const NotificationAlert = () => {
  const { notifications, removeNotification } = useNotifications();
  const timersRef = useRef({});
  const [closingNotifications, setClosingNotifications] = useState(new Set());

  const handleRemove = useCallback(
    (notificationId) => {
      if (timersRef.current[notificationId]) {
        clearTimeout(timersRef.current[notificationId]);
        delete timersRef.current[notificationId];
      }

      // Start exit animation
      setClosingNotifications((prev) => new Set(prev).add(notificationId));

      // Remove notification after animation completes
      setTimeout(() => {
        removeNotification(notificationId);
        setClosingNotifications((prev) => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
      }, 300); // Match animation duration
    },
    [removeNotification]
  );

  useEffect(() => {
    notifications.forEach((notification) => {
      if (notification.autoClose && !timersRef.current[notification.id]) {
        timersRef.current[notification.id] = setTimeout(() => {
          handleRemove(notification.id);
        }, notification.duration);
      }
    });

    const currentNotificationIds = new Set(notifications.map((n) => n.id));
    Object.keys(timersRef.current).forEach((timerId) => {
      if (!currentNotificationIds.has(timerId)) {
        clearTimeout(timersRef.current[timerId]);
        delete timersRef.current[timerId];
      }
    });
  }, [notifications, handleRemove]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="notification-alert-container">
      {notifications.map((notification) => {
        // Sanitize notification data to prevent icon errors
        const notificationType = ['success', 'info', 'warning', 'error'].includes(notification.type)
          ? notification.type
          : 'info';

        const handleNotificationClick = () => {
          if (notification.actionUrl) {
            window.open(notification.actionUrl, '_blank');
          }
        };

        const handleActionClick = (action, event) => {
          event.stopPropagation(); // Prevent notification click
          if (action.actionUrl) {
            window.open(action.actionUrl, '_blank');
          }
        };

        // Render actions at bottom of description
        const renderActions = () => {
          if (!notification.actions || notification.actions.length === 0) {
            return null;
          }

          return (
            <div className="notification-actions-bottom">
              {notification.actions.map((action, index) => (
                <button
                  key={index}
                  className={`notification-action-bottom ${action.type || 'default'}`}
                  onClick={(e) => handleActionClick(action, e)}
                  title={action.label} // Add title attribute for full text on hover
                >
                  {action.label}
                </button>
              ))}
            </div>
          );
        };

        // Render description with HTML support and actions at bottom
        const renderDescription = () => {
          const desc = notification.description || notification.body || '';
          const hasActions = notification.actions && notification.actions.length > 0;

          return (
            <div>
              {notification.messageType === 'html' ? (
                <div dangerouslySetInnerHTML={{ __html: desc }} />
              ) : (
                <div>{desc}</div>
              )}
              {hasActions && renderActions()}
            </div>
          );
        };

        const isClosing = closingNotifications.has(notification.id);

        try {
          return (
            <Alert
              key={notification.id}
              message={notification.title || notification.message || 'Notification'}
              description={renderDescription()}
              type={notificationType}
              showIcon={notification.showIcon}
              closable={notification.closable}
              banner={notification.banner}
              onClose={() => handleRemove(notification.id)}
              className={`notification-alert-item ${
                notification.priority ? `priority-${notification.priority}` : ''
              } ${notification.className || ''} ${
                notification.actionUrl ? 'notification-clickable' : ''
              } ${isClosing ? 'notification-closing' : ''}`}
              style={notification.style}
              onClick={notification.actionUrl ? handleNotificationClick : undefined}
            />
          );
        } catch (error) {
          console.error('Error rendering notification:', error, notification);
          // Return a simple div as fallback
          return (
            <div
              key={notification.id}
              className={`notification-alert-item notification-error ${
                isClosing ? 'notification-closing' : ''
              }`}
              onClick={() => handleRemove(notification.id)}
            >
              <strong>Notification Error</strong>
              <p>Failed to render notification. Click to dismiss.</p>
            </div>
          );
        }
      })}
    </div>
  );
};

export default NotificationAlert;
